/**
 * One tab stop for a whole grid of radio cards, and the arrow keys to move
 * inside it — the WAI-ARIA roving-tabindex pattern, in two dimensions.
 *
 * The marketplace's "all apps" view holds 105 cards; as plain buttons that is
 * 105 Tab presses between the search box and the next step. Here Tab enters the
 * grid once (on the selected card, else the first), the arrows move within it —
 * Up/Down to the card in the next row nearest in x, whatever the column count
 * the container width produced — and Tab leaves it.
 *
 * Arrows move FOCUS, never the selection: choosing an image reprices the order
 * and may raise the disk (which is never lowered back), so walking past forty
 * apps must not choose forty apps. Enter/Space choose, as on any button.
 *
 * Items are found by `data-roving-id` inside `container`; the component binds
 * `:tabindex="id === tabStop ? 0 : -1"` on each.
 */
export function useRovingGrid(
  container: Ref<HTMLElement | null>,
  options: {
    /** Every item id, in DOM order. */
    ids: () => readonly string[]
    /** Where Tab enters when focus has not been inside yet: the selection. */
    preferred: () => string | null
  },
) {
  const focusedId = ref<string | null>(null)

  const tabStop = computed<string | null>(() => {
    const ids = options.ids()
    if (focusedId.value && ids.includes(focusedId.value)) return focusedId.value
    const preferred = options.preferred()
    if (preferred && ids.includes(preferred)) return preferred
    return ids[0] ?? null
  })

  function items(): HTMLElement[] {
    const root = container.value
    if (!root) return []
    return Array.from(root.querySelectorAll<HTMLElement>('[data-roving-id]')).filter(
      // Only what is on screen: a collapsed section's cards are not reachable.
      (el) => el.offsetParent !== null,
    )
  }

  /** The item in the next (or previous) row whose centre is nearest in x. */
  function nearestInRow(all: HTMLElement[], from: HTMLElement, direction: 1 | -1): HTMLElement | undefined {
    const origin = from.getBoundingClientRect()
    const originX = origin.left + origin.width / 2
    const candidates = all
      .map((el) => ({ el, rect: el.getBoundingClientRect() }))
      .filter(({ rect }) => (direction === 1 ? rect.top > origin.top + 4 : rect.top < origin.top - 4))
    if (candidates.length === 0) return undefined
    const rowTop =
      direction === 1
        ? Math.min(...candidates.map(({ rect }) => rect.top))
        : Math.max(...candidates.map(({ rect }) => rect.top))
    return candidates
      .filter(({ rect }) => Math.abs(rect.top - rowTop) < 4)
      .sort(
        (a, b) =>
          Math.abs(a.rect.left + a.rect.width / 2 - originX) - Math.abs(b.rect.left + b.rect.width / 2 - originX),
      )[0]?.el
  }

  function onKeydown(event: KeyboardEvent) {
    const current = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-roving-id]')
    if (!current) return
    const all = items()
    const index = all.indexOf(current)
    if (index === -1) return

    let next: HTMLElement | undefined
    switch (event.key) {
      case 'ArrowRight':
        next = all[index + 1]
        break
      case 'ArrowLeft':
        next = all[index - 1]
        break
      case 'ArrowDown':
        next = nearestInRow(all, current, 1)
        break
      case 'ArrowUp':
        next = nearestInRow(all, current, -1)
        break
      case 'Home':
        next = all[0]
        break
      case 'End':
        next = all[all.length - 1]
        break
      default:
        return
    }
    if (!next) return
    event.preventDefault()
    focusedId.value = next.dataset.rovingId ?? null
    next.focus()
  }

  /** A click or a Tab that lands on an item makes it the tab stop. */
  function onFocusin(event: FocusEvent) {
    const item = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-roving-id]')
    if (item?.dataset.rovingId) focusedId.value = item.dataset.rovingId
  }

  return { tabStop, onKeydown, onFocusin }
}
